import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DebitHistory } from '../../models/finance';
import { AccountLinks, IAccountHolder } from '../../models/user';
import { selectAccountHolderAccountLinks, selectLoggedInGuardian } from '../guardian/guardian.selectors';
import { selectAllStudents } from '../students/student.selectors';
import { FinanceState } from './finance.reducer';

export const selectFinanceState = createFeatureSelector<FinanceState>('finance');

export const selectAllDebitOrders = createSelector(selectFinanceState, (state: FinanceState) => state.debitOrders);

export const selectLoading = createSelector(selectFinanceState, (state: FinanceState) => state.loading);

export const selectError = createSelector(selectFinanceState, (state: FinanceState) => state.error);

export const selectDateRange = createSelector(selectFinanceState, (state: FinanceState) => ({
  fromDate: state.fromDate || new Date(new Date().setFullYear(new Date().getFullYear() - 1)), // Default to one year ago
  toDate: state.toDate || new Date(), // Default to today
}));

export const selectBankAccountsDetails = createSelector(
  selectFinanceState,
  (state: FinanceState) => state.bankDetails || [],
);

export const selectBankRecords = createSelector(selectFinanceState, (state: FinanceState) => state.bankRecords || []);

export const selectSelectedAccountLinks = createSelector(
  selectFinanceState,
  selectAccountHolderAccountLinks,
  (state: FinanceState, accountHolderLinks: AccountLinks[]) => {
    // If no selected account links, return the first account-holder account link
    if (!state.selectedAccountLinks) {
      return accountHolderLinks[0] || { accountCode: '', companyCode: '' };
    }
    // Otherwise, return the selected account links
    return state.selectedAccountLinks;
  },
);

export const selectDebitOrderById = (id: string) =>
  createSelector(selectAllDebitOrders, (debitOrders) => debitOrders.find((debitOrder) => debitOrder.id === id) || null);

// Selects all the debit orders for use in history. Returns an array of objects that include the Date, Amount, SchoolId, and Type of each debit order.
export const selectDebitHistory = createSelector(selectAllDebitOrders, (debitOrders): DebitHistory[] => {
  return debitOrders.flatMap((order) =>
    order.paymentHistory.map((date) => ({
      date,
      amount: order.amount,
      schoolId: order.schoolId,
      type: order.type,
    })),
  );
});

export const selectBankRecordsForAccountLinks = (accountLinks: AccountLinks) =>
  createSelector(selectBankRecords, (bankRecords) => {
    return bankRecords.filter(
      (record) =>
        record.accountLinks.companyCode === accountLinks.companyCode &&
        record.accountLinks.accountCode === accountLinks.accountCode,
    );
  });

export const selectAccountHolderDetails = createSelector(
  selectFinanceState,
  (state: FinanceState) => state.accountHolder || ({} as IAccountHolder),
);

export const accountBursar = createSelector(selectFinanceState, (state: FinanceState) => state.accountBursar || null);

export const selectAccountLearners = (accountLinks: AccountLinks) =>
  createSelector(selectAllStudents, selectBankRecords, (students, bankRecords) => {
    const active = bankRecords.find(
      (record) =>
        record.accountLinks.accountCode === accountLinks.accountCode &&
        record.accountLinks.companyCode === accountLinks.companyCode,
    )?.active;

    return students.filter(
      (student) =>
        student?.school?.myHubCompanySettings?.companyCode === accountLinks.companyCode &&
        student?.school?.accountCode === accountLinks.accountCode &&
        ((student.enrolmentStatus !== 'Inactive' && student.enrolmentStatus !== 'Cancelled') || active),
    );
  });

export const selectBankAccountsWithLearners = createSelector(
  selectBankAccountsDetails,
  selectBankRecords,
  selectAllStudents,
  (bankDetails, bankRecords, students) => {
    return bankDetails.filter((account) => {
      if (!account?.accountCode || !account?.companyCode) return false;

      const active = bankRecords.find(
        (record) =>
          record.accountLinks.accountCode === account.accountCode &&
          record.accountLinks.companyCode === account.companyCode,
      )?.active;

      return students.some(
        (student) =>
          student?.school?.myHubCompanySettings?.companyCode === account.companyCode &&
          student?.school?.accountCode === account.accountCode &&
          ((student.enrolmentStatus !== 'Inactive' && student.enrolmentStatus !== 'Cancelled') || active),
      );
    });
  },
);

export const selectCurrencyCodeForSelectedAccount = createSelector(
  selectSelectedAccountLinks,
  selectBankRecords,
  (selectedLinks, bankRecords) => {
    if (!selectedLinks?.accountCode) return 'ZAR';
    const record = bankRecords.find(
      (r) =>
        r.accountLinks.accountCode === selectedLinks.accountCode &&
        r.accountLinks.companyCode === selectedLinks.companyCode,
    );
    return record?.currencyCode ?? 'ZAR';
  },
);

export const selectLoggedInAccountHolderAccounts = createSelector(
  selectAccountHolderAccountLinks,
  selectLoggedInGuardian,
  selectBankAccountsWithLearners,
  (accountHolderLinks, loggedInGuardian, validAccounts) => {
    if (!accountHolderLinks?.length) return [];

    const validKeys = new Set(validAccounts.map((a) => `${a.accountCode}:${a.companyCode}`));

    return accountHolderLinks
      .filter((link) => validKeys.has(`${link.accountCode}:${link.companyCode}`))
      .map(
        (link) =>
          ({
            accountCode: link.accountCode,
            companyCode: link.companyCode,
            firstName: loggedInGuardian?.firstname || '',
            lastName: loggedInGuardian?.lastname || '',
            title: loggedInGuardian?.title?.description || '',
            email: loggedInGuardian?.primaryEmail?.address || loggedInGuardian?.email || '',
            phoneNumber: loggedInGuardian?.primaryPhone?.number || '',
            pictureUrl: (loggedInGuardian?.pictureUrl as string) || '',
            relationship: loggedInGuardian?.learnerRelationship?.description || '',
            companyName: link.accountHolder?.companyName || '',
          }) satisfies IAccountHolder,
      );
  },
);
